import { useState, useMemo, useCallback } from 'react';
import { getAllProfiles } from '../services/profileService';
import {
  getSavedFilters,
  saveFilter,
  deleteFilter,
} from '../services/savedFiltersService';
import { createFuzzy } from '../utils/fuzzy';
import { buildSearchDocs, SEARCH_KEYS, filterEmployeesBySubstring } from '../utils/searchDocs';

/** All skill-like tags attached to an employee, across every source. */
export function getEmployeeSkillSet(employee, profile = {}) {
  return new Set([
    ...(profile.skills || []),
    ...(profile.areasOfExpertise || []),
    ...(profile.programsExpertise || []),
    ...(employee.skills || []),
  ]);
}

/**
 * Custom hook for search and filter functionality
 * Searches AD data, user-managed profile data, and OpenAsset firm data
 */
export function useSearch(employees) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedOffices, setSelectedOffices] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [savedFilters, setSavedFilters] = useState(() => getSavedFilters());

  // Rebuilt when a search session starts (searchActive flips false→true), so
  // the localStorage profile snapshot is fresh per session without paying an
  // index rebuild on every keystroke.
  const searchActive = Boolean(searchQuery.trim());
  const fuse = useMemo(() => {
    if (!searchActive) return null;
    return createFuzzy(buildSearchDocs(employees, getAllProfiles()), SEARCH_KEYS);
  }, [employees, searchActive]);

  /**
   * Filter employees based on search query and facet filters.
   */
  const { filteredEmployees } = useMemo(() => {
    let results = employees;

    // Snapshot profiles once for the skills facet (fresh localStorage read)
    const allProfiles = selectedSkills.length > 0 ? getAllProfiles() : null;

    // Fuzzy search across name, title, skills, etc. — Fuse returns results
    // ranked by score, so search-hit ordering replaces directory ordering.
    // Fuse's minMatchCharLength (2) returns nothing for a 1-char query, which
    // would empty the whole directory on the very first keystroke — fall back
    // to a plain substring match over identity fields for that case.
    const trimmedQuery = searchQuery.trim();
    if (trimmedQuery.length === 1) {
      results = filterEmployeesBySubstring(employees, trimmedQuery);
    } else if (trimmedQuery && fuse) {
      results = fuse.search(trimmedQuery).map((r) => r.item.employee);
    }

    // Filter by selected departments
    if (selectedDepartments.length > 0) {
      results = results.filter((employee) =>
        selectedDepartments.includes(employee.department)
      );
    }

    // Filter by selected offices
    if (selectedOffices.length > 0) {
      results = results.filter((employee) =>
        selectedOffices.includes(employee.officeLocation)
      );
    }

    // Filter by selected skill tags (any-of)
    if (selectedSkills.length > 0) {
      results = results.filter((employee) => {
        const skillSet = getEmployeeSkillSet(employee, allProfiles[employee.id] || {});
        return selectedSkills.some((skill) => skillSet.has(skill));
      });
    }

    return { filteredEmployees: results };
  }, [employees, searchQuery, fuse, selectedDepartments, selectedOffices, selectedSkills]);

  /**
   * Group employees by department
   */
  const groupedByDepartment = useMemo(() => {
    const groups = {};

    filteredEmployees.forEach((employee) => {
      const dept = employee.department || 'Unassigned';
      if (!groups[dept]) {
        groups[dept] = [];
      }
      groups[dept].push(employee);
    });

    // Sort departments alphabetically
    return Object.keys(groups)
      .sort()
      .reduce((acc, key) => {
        acc[key] = groups[key];
        return acc;
      }, {});
  }, [filteredEmployees]);

  /**
   * Toggle department filter
   */
  const toggleDepartment = useCallback((department) => {
    setSelectedDepartments((prev) =>
      prev.includes(department)
        ? prev.filter((d) => d !== department)
        : [...prev, department]
    );
  }, []);

  /**
   * Toggle office filter
   */
  const toggleOffice = useCallback((office) => {
    setSelectedOffices((prev) =>
      prev.includes(office)
        ? prev.filter((o) => o !== office)
        : [...prev, office]
    );
  }, []);

  /**
   * Toggle skill tag filter
   */
  const toggleSkill = useCallback((skill) => {
    setSelectedSkills((prev) =>
      prev.includes(skill)
        ? prev.filter((s) => s !== skill)
        : [...prev, skill]
    );
  }, []);

  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    setSearchQuery('');
    setSelectedDepartments([]);
    setSelectedOffices([]);
    setSelectedSkills([]);
  }, []);

  /**
   * Check if any filters are active
   */
  const hasActiveFilters = searchQuery.trim() !== '' || selectedDepartments.length > 0 || selectedOffices.length > 0 || selectedSkills.length > 0;

  /**
   * Save the current query + department + office state as a named preset.
   * @param {string} name
   */
  const saveCurrentFilter = useCallback((name) => {
    const newFilter = saveFilter({
      name,
      query: searchQuery,
      departments: selectedDepartments,
      offices: selectedOffices,
      skills: selectedSkills,
    });
    setSavedFilters((prev) => [...prev, newFilter]);
  }, [searchQuery, selectedDepartments, selectedOffices, selectedSkills]);

  /**
   * Delete a saved filter preset and sync state.
   * @param {string} id
   */
  const deleteSavedFilter = useCallback((id) => {
    deleteFilter(id);
    setSavedFilters((prev) => prev.filter((f) => f.id !== id));
  }, []);

  /**
   * Apply a saved filter preset — sets query, departments, and offices.
   * @param {{ query: string, departments: string[], offices: string[] }} filter
   */
  const applyFilter = useCallback((filter) => {
    setSearchQuery(filter.query || '');
    setSelectedDepartments(filter.departments || []);
    setSelectedOffices(filter.offices || []);
    setSelectedSkills(filter.skills || []);
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    selectedDepartments,
    setSelectedDepartments,
    toggleDepartment,
    selectedOffices,
    setSelectedOffices,
    toggleOffice,
    selectedSkills,
    setSelectedSkills,
    toggleSkill,
    filteredEmployees,
    groupedByDepartment,
    clearFilters,
    hasActiveFilters,
    savedFilters,
    saveCurrentFilter,
    deleteSavedFilter,
    applyFilter,
  };
}
